import { Button, EmptyState, ErrorState, LoadingSkeleton, buttonProps } from "@a2z/ui";
import { useDeferredValue, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CircleSlash, SquareArrowOutUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n, useTranslate } from "@/lib/i18n";
import type { KitchenBoardResponse, KitchenBoardTab, KitchenStation } from "@/lib/api/types";
import { PERMISSIONS, hasPermission, useSession } from "@/modules/auth/useSession";
import { useErrorMessage } from "@/shared/errors";
import { queryKeys, useApiQuery, useObservedQueryData } from "@/shared/queries";
import { endpoints } from "@/lib/api/endpoints";
import { useKitchenBoard } from "../lib/useKitchenBoard";
import { kitchenTabCount } from "./PaperKitchen";
import { KitchenLanes } from "./KitchenLanes";
import { KitchenRecallDrawer } from "./KitchenRecallDrawer";
import { boardWorkflow } from "../lib/workflow";
import { struckVoids } from "../lib/voids";
import { readyAgeOffMinutesOf, splitAgedOff, visibleRetained } from "../lib/ageOff";
import {
  KITCHEN_HIDDEN_PAID_KEY,
  KITCHEN_PINNED_READY_KEY,
  useKitchenDeviceMarks,
} from "../lib/kitchenDeviceMarks";
import { useCoarsePointer } from "../lib/kitchenSizePreference";


/**
 * THE FLOOR'S KITCHEN TAB (2026-10-03) — whether it exists, and its count.
 *
 * Only for a session that may read the board (`RESTAURANT_KITCHEN_VIEW`, the
 * board endpoint's own gate) and only on a store that runs the kitchen display
 * — the same two things `/kitchen` checks. `enabled` is the floor's own gate
 * (`POS_USE`), so a cook who lands on the floor URL fetches nothing here.
 *
 * P7.1: off the slim tab read (`GET /kitchen-board/tab`), the board's own
 * figures without its plates — until then the floor and the till read the
 * WHOLE board on every kitchen signal for this badge. While the Kitchen tab
 * shows every station, the whole-kitchen board is already being read on
 * screen, so the count comes off that read and the tab read waits; the moment
 * nothing on screen reads it, the tab read takes over.
 */
export function useFloorKitchenTab(enabled: boolean): {
  available: boolean;
  count: number;
  waiting: boolean;
} {
  const { data: session } = useSession();
  const canView = enabled && hasPermission(session, PERMISSIONS.KITCHEN_VIEW);
  const watched = useObservedQueryData<KitchenBoardResponse>(queryKeys.kitchenBoard(null));
  // A board opened with the page joins the cache only after the first
  // commit, so the tab read waits one deferred pass rather than going out
  // beside it.
  const wantTab = useDeferredValue(canView && !watched.observed, false);
  const tab = useKitchenBoardTab(wantTab);
  if (watched.observed && watched.data) {
    return {
      available: canView && watched.data.kitchenDisplayEnabled === true,
      ...kitchenTabCount(watched.data.plates),
    };
  }
  return {
    available: canView && tab.data?.kitchenDisplayEnabled === true,
    count: tab.data?.count ?? 0,
    waiting: tab.data?.waiting ?? false,
  };
}

/** The slim tab read (P7.1), on the board's own refresh triggers. */
function useKitchenBoardTab(enabled: boolean) {
  return useApiQuery<KitchenBoardTab>(
    queryKeys.kitchenBoardTab,
    endpoints.restaurant.kitchenBoardTab,
    {
      // Availability owns navigation on Layout/QR as well as the live floor.
      enabled,
      refetchOnWindowFocus: true,
    },
  );
}

/**
 * The kitchen board inside the floor page: the PAPER board's three lanes of
 * ticket cards, with the same data, commands and chit printing as `/kitchen`
 * (`useKitchenBoard`). Rendered only behind `useFloorKitchenTab().available`.
 *
 * It opens on every station. Its toolbar is the wall screen's own door set,
 * in the floor's quiet register (founder, 2026-10-03: "lose no function"):
 * the station chips master's board had (picked here for this tab only, never
 * re-pointing the device's `/kitchen`), then "Out today" — the 86 screen, on
 * the board's own gate — and the way to the full board. Without them an
 * owner or manager on a desktop had no path to either: the PAPER sidebar
 * drops its Kitchen row for anyone who has this tab.
 */
export function PaperKitchenView() {
  const t = useTranslate();
  const { locale } = useI18n();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const { data: session } = useSession();
  const canView = hasPermission(session, PERMISSIONS.KITCHEN_VIEW);
  const canPrepare = hasPermission(session, PERMISSIONS.ITEM_PREPARE);
  const canServe = hasPermission(session, PERMISSIONS.ITEM_SERVE);
  // `/kitchen`'s own gate on its "Out today" entry (D50).
  const canToggleAvailability = hasPermission(session, PERMISSIONS.MENU_AVAILABILITY);
  const {
    board,
    plates,
    stationList,
    stationId,
    setStationId,
    canShowAllStations,
    stationGone,
    setStationGone,
    busyItemIds,
    now,
    runWithUndo,
    undo,
  } = useKitchenBoard({ enabled: canView, remember: false });
  /* The store's workflow decides the lanes (D170), and the floor draws them
     through the wall board's own `KitchenLanes` — under the wall board's own
     display rules (gate P4 #3): a dine-in READY plate leaves the lanes N
     minutes after it was ready (T2, per plate as on the station view; an
     off-premise bag never, N = 0 never), and the same recall drawer brings it
     back — «تقديم» or «إظهار», pinned on this device with the same marks —
     so nothing aged off is out of reach here. Voids are struck, and every
     plate a close collapsed while it cooked is drawn, worded by its bill. */
  const workflow = boardWorkflow(board.data);
  const ageOffMinutes = readyAgeOffMinutesOf(board.data?.readyAgeOffMinutes);
  const pinnedReady = useKitchenDeviceMarks(KITCHEN_PINNED_READY_KEY);
  const { live, agedOff } = splitAgedOff(plates, ageOffMinutes, now, pinnedReady.ids);
  const voided = struckVoids(board.data?.recentlyVoided, now);
  const hiddenPaid = useKitchenDeviceMarks(KITCHEN_HIDDEN_PAID_KEY);
  const paid = visibleRetained(board.data?.retainedPaid, ageOffMinutes, now, hiddenPaid.ids);
  const hasCards = live.length > 0 || voided.length > 0 || paid.length > 0;
  const [recallOpen, setRecallOpen] = useState(false);
  const coarsePointer = useCoarsePointer();

  /* The chips list the WHOLE kitchen — every station and which of them still
     have food in flight — so the row stays put while a picked station loads,
     and still lists the stations it is not showing. On every station that is
     the board on screen; on a picked one it is the tab read the floor's count
     already makes (same key, so no extra request) — not, as until P7.1, a
     second whole board beside the station's. */
  const tab = useKitchenBoardTab(canView && stationId !== null);
  // Until the first tab read lands, the whole board this tab last read keeps
  // the row where it was.
  const lastWhole =
    stationId !== null && !tab.data
      ? queryClient.getQueryData<KitchenBoardResponse>(queryKeys.kitchenBoard(null))
      : undefined;
  const allStations =
    (stationId === null
      ? board.data?.stations
      : (tab.data?.stations ?? lastWhole?.stations)) ?? stationList;
  const inFlight = new Set(
    stationId !== null && tab.data
      ? tab.data.inFlightStationIds
      : (lastWhole?.plates ?? plates).map((plate) => plate.stationId),
  );
  const stationLabel = (station: KitchenStation) =>
    locale === "en" && station.nameEn ? station.nameEn : station.nameAr;
  // `/kitchen`'s rule: a paused station keeps its chip while its plates are
  // still in flight, so no food is stranded where only "All" can see it.
  const chips = allStations.filter((one) => one.isActive || inFlight.has(one.id));

  return (
    /* The floor tab has no size setting of its own (that is the wall board's,
       per device): it keeps the board's tablet sizes on a coarse pointer, as
       before, through the same attribute the board's CSS reads (P5, 5.1). */
    <div
      data-ui="floor-kitchen"
      data-kitchen-size={coarsePointer ? "touch" : undefined}
      className="flex flex-col gap-4"
    >
      <div data-ui="kitchen-toolbar" className="no-print flex flex-wrap items-center gap-1.5">
        {chips.length > 0 ? (
          <div
            role="group"
            aria-label={t((m) => m.kitchen.stationPickerLabel)}
            className="flex flex-wrap items-center gap-1.5"
          >
            {/* D128 — a role kept to one station has no "all" to offer. */}
            {[...(canShowAllStations ? [null] : []), ...chips].map((station) => {
              const id = station?.id ?? null;
              const isActive = stationId === id;
              return (
                <button
                  key={id ?? "all"}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => {
                    setStationId(id);
                    setStationGone(false);
                  }}
                  data-ui="station-chip"
                  data-active={isActive ? "" : undefined}
                  data-testid={`floor-kitchen-station-${id ?? "all"}`}
                  className="whitespace-nowrap transition-colors"
                >
                  {station
                    ? station.isActive
                      ? stationLabel(station)
                      : `${stationLabel(station)} ${t((m) => m.manage.stationPausedSuffix)}`
                    : t((m) => m.kitchen.allStations)}
                </button>
              );
            })}
          </div>
        ) : null}
        <span className="flex-1" />
        {/* «المُنجز مؤخرًا» — the wall board's recall, for whoever may cook
            or serve (gate P4 #3). */}
        {canServe || canPrepare ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid="floor-kitchen-recall-open"
            data-count={agedOff.length + (board.data?.recentServed?.length ?? 0)}
            onClick={() => setRecallOpen(true)}
          >
            {t((m) => m.kitchen.recallOpen)}
          </Button>
        ) : null}
        {canToggleAvailability ? (
          <Link
            to={`/${locale}/kitchen/availability`}
            data-testid="floor-kitchen-availability"
            {...buttonProps({ variant: "ghost", size: "sm", className: "gap-1.5" })}
          >
            <CircleSlash size={14} strokeWidth={1.75} aria-hidden="true" />
            {t((m) => m.kitchen.availabilityEntry)}
          </Link>
        ) : null}
        <Link
          to={`/${locale}/kitchen`}
          data-testid="floor-kitchen-open-board"
          {...buttonProps({ variant: "ghost", size: "sm", className: "gap-1.5" })}
        >
          <SquareArrowOutUpRight size={14} strokeWidth={1.75} aria-hidden="true" />
          {t((m) => m.paperKitchen.openBoard)}
        </Link>
      </div>
      {stationGone ? (
        <p role="status" data-ui="kitchen-station-gone" data-testid="floor-kitchen-station-gone">
          {t((m) => m.kitchen.stationGoneNotice)}
        </p>
      ) : null}
      {board.isLoading ? (
        <div aria-busy="true" aria-live="polite" aria-label={t((m) => m.common.loading)}>
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}
      {board.error ? (
        <ErrorState
          message={errorMessage(board.error)}
          retryLabel={t((m) => m.common.retry)}
          onRetry={() =>
            void queryClient.invalidateQueries({ queryKey: queryKeys.kitchenBoard(stationId) })
          }
        />
      ) : null}
      {board.data && !hasCards ? (
        <EmptyState
          title={t((m) => m.paperFloor.kitchenEmpty)}
          description={
            stationId === null
              ? t((m) => m.paperKitchen.emptyAllBody)
              : t((m) => m.kitchen.emptyBody)
          }
        />
      ) : null}
      {hasCards ? (
        <div data-ui="kitchen-board-row" className="flex flex-col gap-3">
        {/* «بانتظار أمين الصندوق» left this tab for the till floor's Tables view
            (T8, D173): it is the person who fires who answers it. */}
        <KitchenLanes
          testId="floor-kitchen-lanes"
          workflow={workflow}
          plates={live}
          voided={voided}
          paid={paid}
          now={now}
          busyItemIds={busyItemIds}
          canPrepare={canPrepare}
          canServe={canServe}
          onRun={runWithUndo}
          onHidePaid={hiddenPaid.add}
        />
        </div>
      ) : null}
      <KitchenRecallDrawer
        isOpen={recallOpen}
        onClose={() => setRecallOpen(false)}
        recentServed={board.data?.recentServed ?? []}
        agedOff={agedOff}
        now={now}
        canServe={canServe}
        busyItemIds={busyItemIds}
        onUndoServed={(targets) => void undo(targets, "undoServed")}
        onServe={(targets) => void runWithUndo(targets, "markServed")}
        onShow={(ids) => pinnedReady.add(ids)}
      />
    </div>
  );
}
